import { Link, useNavigate } from 'react-router-dom';
import { Users, Bell } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { NOTIFICATION_TYPES, getNotificationTarget } from '../utils/notifications';
import Button from './ui/Button';
import Avatar from './Avatar';
import useFollowBack from '../hooks/useFollowBack';
import { describeNotification, notificationPreview, shortAge } from '../utils/notificationCopy';
import { GLASS_SURFACES, CARD_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import { useNotificationsList } from '../hooks/queries/useNotificationsQueries';
import { usePromptAuth } from './auth/AuthPrompt';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '../hooks/mutations/useNotificationMutations';

function formatDate(date) {
  const then = new Date(date);
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const dateStr = then.toDateString();

  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function groupNotificationsByDate(notifications) {
  const groups = {};

  notifications.forEach((n) => {
    const date = formatDate(n.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(n);
  });

  return Object.entries(groups).map(([date, notifications]) => ({
    date,
    notifications,
  }));
}

/** Bold names inside one line of copy (describeNotification's parts). */
export function NotificationText({ parts }) {
  return parts.map((p, i) =>
    typeof p === 'string' ? (
      <span key={i}>{p}</span>
    ) : (
      <strong key={i} className="font-semibold text-ink">
        {p.strong}
      </strong>
    ),
  );
}

/**
 * A person is a round avatar (tap: their profile); a group is a square tile (operator, 2026-10-04: a follow and a
 * group event must look different at a glance).
 */
export function NotificationMark({ notification, kind, size = 'sm' }) {
  const actor = notification.actor;
  if (kind === 'person' && actor) {
    return (
      <Link
        to={`/profile/${actor.id}`}
        className="shrink-0 rounded-full"
        aria-label={`${actor.display_name}'s profile`}
        onClick={(e) => e.stopPropagation()}
      >
        <Avatar
          displayName={actor.display_name}
          profilePhotoUrl={actor.profile_photo_url}
          size={size}
        />
      </Link>
    );
  }
  return (
    <span className="notif-tile" aria-hidden="true">
      {kind === 'group' ? <Users className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
    </span>
  );
}

function NotificationItem({ notification, onMarkAsRead, isFollowing, followBack }) {
  const navigate = useNavigate();
  const { kind, parts } = describeNotification(notification);
  const preview = notificationPreview(notification);
  const actorId = notification.actor?.id;
  const canFollowBack =
    notification.type === NOTIFICATION_TYPES.FOLLOW && actorId && !isFollowing(actorId);

  const open = () => {
    if (!notification.read) onMarkAsRead(notification);
    const target = getNotificationTarget(notification);
    if (target) navigate(target);
  };

  return (
    <div className={`notif-row${notification.read ? '' : ' notif-row--unread'}`}>
      <NotificationMark notification={notification} kind={kind} />
      <button type="button" className="notif-row__main" onClick={open}>
        <span className="notif-row__text">
          <NotificationText parts={parts} />
        </span>
        {preview && <span className="notif-row__preview">{preview}</span>}
      </button>
      {canFollowBack ? (
        <Button
          type="button"
          size="sm"
          className="shrink-0 !rounded-full !px-3"
          onClick={() => {
            if (!notification.read) onMarkAsRead(notification);
            void followBack(actorId);
          }}
        >
          Follow back
        </Button>
      ) : (
        <span className="notif-row__age">{shortAge(notification.created_at)}</span>
      )}
    </div>
  );
}

// Dev only: ?sample=1 fills the page with one of each kind, so the layout can be checked signed out.
function sampleNotifications() {
  if (!import.meta.env.DEV || !new URLSearchParams(window.location.search).get('sample'))
    return null;
  const at = (mins) => new Date(Date.now() - mins * 60000).toISOString();
  const p = (id, name) => ({ id, display_name: name, profile_photo_url: null });
  const g = { id: 'g1', name: 'Portola group' };
  return [
    { id: 's1', type: 'follow', actor: p('u1', 'Maya'), created_at: at(4), read: false },
    {
      id: 's2',
      type: 'join_request',
      actor: p('u2', 'Jordan'),
      group: g,
      created_at: at(35),
      read: false,
    },
    { id: 's3', type: 'follow', actor: p('u3', 'Riley'), created_at: at(120), read: true },
    {
      id: 's4',
      type: 'group_message',
      title: '3 new messages in Portola group',
      message: 'meet at the ferris wheel after Fred',
      group: g,
      created_at: at(300),
      read: true,
    },
    {
      id: 's5',
      type: 'join_approved',
      group: { id: 'g2', name: 'CRSSD group' },
      created_at: at(1500),
      read: true,
    },
    {
      id: 's6',
      type: 'member_joined',
      actor: p('u4', 'Taylor'),
      group: g,
      created_at: at(1600),
      read: true,
    },
    { id: 's7', type: 'promoted_to_manager', group: g, created_at: at(4000), read: true },
  ];
}

export default function Notifications() {
  const promptAuth = usePromptAuth();
  const { session } = useCurrentUserProfile();
  const filter = 'all';
  const { isFollowing, followBack } = useFollowBack();
  const userId = session?.user?.id;

  const sample = sampleNotifications();
  const listQuery = useNotificationsList(userId, filter);
  const notifications = sample ?? listQuery.data ?? [];
  const loading = !sample && listQuery.isPending;
  const markAsReadMutation = useMarkNotificationRead(userId);
  const markAllAsReadMutation = useMarkAllNotificationsRead(userId);

  const markAsRead = (notification) => {
    if (!userId || !notification?.id) return;
    markAsReadMutation.mutate({ notification, notifications, filter });
  };

  const markAllAsRead = () => {
    if (!userId) return;
    markAllAsReadMutation.mutate({ filter });
  };

  if (!session && !sample) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center py-24">
          <Bell className="w-16 h-16 mx-auto text-gray-300 dark:text-dark-muted mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">
            Log in to view notifications
          </h2>
          <p className="text-gray-500 dark:text-dark-muted mb-6">
            See who followed you and what your groups are up to.
          </p>
          <Button size="lg" onClick={() => promptAuth()}>
            Log in
          </Button>
        </div>
      </div>
    );
  }

  const groupedNotifications = groupNotificationsByDate(notifications);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="min-w-0 truncate text-2xl sm:text-3xl font-bold text-gray-900 dark:text-dark-text">
          Notifications
        </h1>
        {unreadCount > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={markAllAsRead}
            disabled={markAllAsReadMutation.isPending}
            className="shrink-0 whitespace-nowrap"
          >
            Mark all read
          </Button>
        )}
      </div>

      <div
        className={`overflow-hidden rounded-xl ${
          GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
        }`}
      >
        {loading ? (
          <div className="divide-y divide-white/5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 animate-pulse" aria-hidden>
                <div className="h-10 w-10 rounded-full skeleton-shimmer shrink-0" />
                <div className="flex-1 min-w-0 space-y-2 pt-1">
                  <div className="h-3.5 w-2/5 rounded skeleton-shimmer" />
                  <div className="h-3 w-full rounded skeleton-shimmer" />
                  <div className="h-2.5 w-16 rounded skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-gray-300 dark:text-dark-border mb-3" />
            <p className="text-gray-500 dark:text-dark-muted">No notifications yet</p>
          </div>
        ) : (
          groupedNotifications.map((group) => (
            <div key={group.date}>
              <div className="border-b border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase text-gray-500 dark:text-dark-muted">
                {group.date}
              </div>
              {group.notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  isFollowing={isFollowing}
                  followBack={followBack}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
